import React from "react";
import { useIntl } from "react-intl";
import IOrder_Progress_State from "../../../../models/IOrder_Progress_State";
import ISupplierModel from "../../../../models/ISupplierModel";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import { ATTACHMENT_KEYS, getStepChipSpecs, holdsAt, RECIPIENT_KEYS, splitEmails, vendorKey } from "./workflowSteps";

type Props = {
    step: IOrder_Progress_State;
    allSteps: IOrder_Progress_State[];
    supplierModel: ISupplierModel;
};

/** Three short columns restating the selected step in plain words. */
export const WorkflowStepGuide = (props: Props) => {
    const intl = useIntl();
    const supplier = props.supplierModel;
    const t = (key: string, values?: Record<string, any>) => {
        const id = vendorKey(key, supplier);

        return intl.formatMessage({ id, defaultMessage: `_${id}_` }, values);
    };

    const { step, allSteps } = props;
    const index = allSteps.findIndex((candidate) => candidate.order_state_id === step.order_state_id);
    const nextStep = index >= 0 && index + 1 < allSteps.length ? allSteps[index + 1] : null;
    const stepName = t(step.name);

    // Who can edit
    const roles: { labelKey: string; readOnly: boolean }[] = [
        { labelKey: "internal_admins", readOnly: holdsAt(allSteps, step, "isSupplierAdminReadOnly") },
        { labelKey: "internal_users", readOnly: holdsAt(allSteps, step, "isSupplierReadOnly") },
    ];

    if (supplier.has_customer_module) {
        roles.push({ labelKey: "vendors", readOnly: holdsAt(allSteps, step, "isVendorReadOnly") });
    }

    const canEdit = roles.filter((role) => !role.readOnly).map((role) => t(role.labelKey));
    const readOnly = roles.filter((role) => role.readOnly).map((role) => t(role.labelKey));

    // On arrival
    const arrival: string[] = [];

    if (step.sendEmail) {
        const recipients = RECIPIENT_KEYS.filter((recipient) => step[recipient.key] && (!recipient.when || recipient.when(supplier))).map(
            (recipient) => t(recipient.labelKey)
        );
        const extra = splitEmails(step.email);
        const attachments = ATTACHMENT_KEYS.filter((attachment) => step[attachment.key]).map((attachment) => t(attachment.labelKey));
        const who = [...recipients, ...extra];

        arrival.push(who.length ? t("guide_emails", { recipients: who.join(", ") }) : t("chip_emails_nobody"));

        if (attachments.length) {
            arrival.push(t("guide_attached", { attachments: attachments.join(", ") }));
        }
    }

    getStepChipSpecs(step, supplier)
        .filter(
            (chip) =>
                !["email", "noemail", "admin", "vendorstep", "vro", "uro", "aro", "custom", "split", "first", "everyone"].includes(chip.id)
        )
        .filter((chip) => !chip.id.startsWith("attach"))
        .forEach((chip) => arrival.push(t(chip.labelKey)));

    // Who moves it on
    const movers: string[] = [];

    if (nextStep) {
        movers.push(nextStep.isAdminOnlyStep ? t("internal_admins_only") : t("internal_admins_and_users"));

        if (supplier.has_customer_module && nextStep.isVendorStep) {
            movers.push(t("vendors"));
        }
    }

    const Column = (columnProps: {
        titleKey: string;
        titleValues?: Record<string, any>;
        lines: string[];
        emptyKey?: string;
        noteKey?: string;
    }) => (
        <div className={"WorkflowGuide-col"}>
            <b>{t(columnProps.titleKey, columnProps.titleValues)}</b>
            <ul>
                {columnProps.lines.length ? (
                    columnProps.lines.map((line, i) => <li key={i}>{line}</li>)
                ) : columnProps.emptyKey ? (
                    <li>{t(columnProps.emptyKey)}</li>
                ) : null}
                {columnProps.noteKey ? (
                    <li className={"text-muted"}>
                        <OwLabel title={columnProps.noteKey} noBottomMargin={true} />
                    </li>
                ) : null}
            </ul>
        </div>
    );

    return (
        <div className={"WorkflowGuide"} data-testid={"wf-guide"}>
            <div className={"WorkflowGuide-head"}>
                <span className={"WorkflowEditor-caption"}>{t("at_step_this_means", { step: stepName })}</span>
                <span className={"WorkflowGuide-rule"} />
            </div>
            <div className={"WorkflowGuide-cols"}>
                <Column
                    titleKey={"who_can_edit_the_order"}
                    lines={[...(canEdit.length ? [canEdit.join(", ")] : []), ...readOnly.map((role) => t("guide_read_only", { role }))]}
                />
                <Column titleKey={"on_arrival"} lines={arrival} emptyKey={"nothing_automatic"} />
                {nextStep ? (
                    <Column
                        titleKey={"who_moves_it_to"}
                        titleValues={{ step: t(nextStep.name) }}
                        lines={movers}
                        noteKey={"admins_can_always_jump"}
                    />
                ) : (
                    <Column titleKey={"what_comes_next"} lines={[t("this_is_the_last_step")]} noteKey={"admins_can_always_jump"} />
                )}
            </div>
        </div>
    );
};

export default WorkflowStepGuide;
